import React, { useEffect, useRef, useState } from "react";
import { base44 } from "@/api/base44Client";
import { useOrganisation } from "@/lib/OrganisationContext";
import {
  FileText,
  Loader2,
  Copy,
  Check,
  Sparkles,
  Briefcase,
  BarChart3,
  ClipboardList,
  ShieldCheck,
} from "lucide-react";
import ExampleDocuments from "@/components/esg/ExampleDocuments";

const DOC_TYPES = [
  {
    id: "tender",
    label: "Tender Response",
    icon: Briefcase,
    placeholder:
      "Paste the tender question or social-value requirement you need to answer…",
    hint: "Generates a structured, evidence-led response referencing your Disability Confident status.",
  },
  {
    id: "esg",
    label: "ESG Report",
    icon: BarChart3,
    placeholder:
      'Describe the ESG section you need (e.g. "Social — disability inclusion narrative for annual report")…',
    hint: "Generates a board-ready ESG narrative section with performance highlights and governance.",
  },
  {
    id: "board",
    label: "Board Pack Insert",
    icon: ClipboardList,
    placeholder:
      'Describe the board update you need (e.g. "Quarterly disability inclusion progress update")…',
    hint: "Generates a one-page board pack insert with progress, risks and decisions requested.",
  },
  {
    id: "policy",
    label: "Policy",
    icon: ShieldCheck,
    placeholder: "Describe the disability inclusion policy you need to draft…",
    hint: "Generates a practical policy using the selected organisation’s verified context.",
  },
];

export default function ESGBids() {
  const { selectedOrg } = useOrganisation();
  const [activeType, setActiveType] = useState("tender");
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  const action = useRef(0);
  const tenantRef = useRef(null);
  const resultRef = useRef(null);
  const promptRef = useRef(null);
  const resultHeadingRef = useRef(null);
  const copyTimer = useRef(null);
  tenantRef.current = selectedOrg?.id || null;
  resultRef.current = result;

  useEffect(() => { if (result) resultHeadingRef.current?.focus(); }, [result]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (copyTimer.current) clearTimeout(copyTimer.current);
    };
  }, []);

  useEffect(() => {
    action.current += 1;
    setGenerating(false);
    setResult(null);
    setError(null);
    setPrompt("");
    setCopied(false);
    if (copyTimer.current) {
      clearTimeout(copyTimer.current);
      copyTimer.current = null;
    }
  }, [selectedOrg?.id]);

  async function handleGenerate(event) {
    event?.preventDefault();
    if (generating) return;
    if (!prompt.trim()) {
      setError("Enter what you need the document to cover.");
      promptRef.current?.focus();
      return;
    }
    const token = ++action.current;
    const targetId = tenantRef.current;
    setGenerating(true);
    setResult(null);
    setError(null);
    setCopied(false);
    try {
      const res = await base44.integrations.Core.GenerateDocument({
        kind: activeType,
        brief: prompt.trim(),
      });
      if (
        !res ||
        typeof res.title !== "string" ||
        typeof res.content !== "string"
      )
        throw new Error("The server returned an invalid document.");
      if (
        !mounted.current ||
        action.current !== token ||
        tenantRef.current !== targetId
      )
        return;
      setResult({ title: res.title, content: res.content });
    } catch (e) {
      console.error(e);
      if (
        !mounted.current ||
        action.current !== token ||
        tenantRef.current !== targetId
      )
        return;
      setError(e?.code === "ai_unavailable"
        ? "Document generation is unavailable right now. No document was generated. Your request remains in this form; try again when the service is available."
        : "No document was generated. Your request remains in this form; check it and try again.");
    } finally {
      if (
        mounted.current &&
        action.current === token &&
        tenantRef.current === targetId
      )
        setGenerating(false);
    }
  }

  async function copyResult() {
    if (!result?.content) return;
    const targetResult = result;
    const token = action.current;
    const targetId = tenantRef.current;
    try {
      await navigator.clipboard.writeText(targetResult.content);
      if (
        !mounted.current ||
        action.current !== token ||
        tenantRef.current !== targetId ||
        resultRef.current !== targetResult
      )
        return;
      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => {
        if (
          mounted.current &&
          action.current === token &&
          tenantRef.current === targetId &&
          resultRef.current === targetResult
        ) {
          setCopied(false);
          copyTimer.current = null;
        }
      }, 1500);
    } catch {
      if (
        mounted.current &&
        action.current === token &&
        tenantRef.current === targetId &&
        resultRef.current === targetResult
      )
        setError("Unable to copy the document.");
    }
  }

  const activeDoc = DOC_TYPES.find((d) => d.id === activeType);

  return (
    <div className="esg-page space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="esg-page-title text-2xl font-bold text-foreground">ESG &amp; Bids</h1>
        <p className="text-sm mt-1">
          Generate ESG reporting, tender responses, board pack inserts and
          policies from your Disability Confident evidence.
        </p>
      </div>

      {/* Doc type selector */}
      <div
        className="esg-tabs flex flex-wrap gap-2"
        role="group"
        aria-label="Document type"
      >
        {DOC_TYPES.map((d) => {
          const Icon = d.icon;
          const active = d.id === activeType;
          return (
            <button
              key={d.id}
              aria-pressed={active}
              onClick={() => {
                action.current += 1;
                setGenerating(false);
                setActiveType(d.id);
                setResult(null);
                setError(null);
                setPrompt("");
                setCopied(false);
                if (copyTimer.current) {
                  clearTimeout(copyTimer.current);
                  copyTimer.current = null;
                }
              }}
              className={`flex min-h-11 items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring ${
                active
                  ? "esg-tab-active bg-teal-900 text-white"
                  : "bg-card border border-border text-foreground hover:bg-muted"
              }`}
            >
              <Icon className="w-4 h-4" aria-hidden="true" />
              {d.label}
            </button>
          );
        })}
      </div>

      {/* Generator */}
      <form onSubmit={handleGenerate} className="esg-generator-card bg-card border border-border rounded-xl p-5 shadow-sm space-y-4" noValidate>
        <div>
          <label
            htmlFor="esg-prompt"
            className="block text-sm font-semibold text-foreground mb-1"
          >
            {activeDoc.label} request
          </label>
          <p className="text-xs mb-2">{activeDoc.hint}</p>
          <p id="esg-prompt-help" className="mb-2 text-sm text-muted-foreground">Required. Describe the question, audience and points the document should cover.</p>
          <textarea
            ref={promptRef}
            id="esg-prompt"
            value={prompt}
            maxLength={6000}
            required
            aria-describedby="esg-prompt-help esg-prompt-count"
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={activeDoc.placeholder}
            rows={4}
            className="w-full resize-y bg-muted/40 text-foreground placeholder:text-muted-foreground text-sm rounded-lg px-3 py-2.5 border border-border focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
          />
          <p id="esg-prompt-count" className="mt-1 text-xs text-muted-foreground">{prompt.length} of 6,000 characters</p>
        </div>
        <button
          type="submit"
          disabled={generating}
          className="esg-primary-action flex min-h-11 items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-900 text-white text-sm font-semibold hover:bg-teal-950 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-400"
        >
          {generating ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : (
            <Sparkles className="w-4 h-4" aria-hidden="true" />
          )}
          {generating ? "Generating…" : `Generate ${activeDoc.label}`}
        </button>
        {generating && <p role="status" aria-live="polite" className="text-sm text-muted-foreground">Generating {activeDoc.label}. This may take a moment.</p>}
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-700 dark:bg-red-950/30 dark:text-red-200"
          >
            <p>{error}</p>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating}
              className="mt-2 min-h-11 px-2 font-semibold underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            >
              Try again
            </button>
          </div>
        )}
      </form>

      {/* Result */}
      {result && (
        <section className="esg-result-card bg-card border border-border rounded-xl p-5 shadow-sm" aria-labelledby="generated-document-title">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-teal-600" aria-hidden="true" />
              <h2 id="generated-document-title" ref={resultHeadingRef} tabIndex={-1} className="text-sm font-semibold text-foreground">
                {result.title || "Generated document"}
              </h2>
            </div>
            <button
              onClick={copyResult}
              className="flex min-h-11 items-center gap-1.5 px-2 text-xs font-medium text-teal-700 hover:text-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring rounded"
            >
              {copied ? (
                <Check className="w-3.5 h-3.5" aria-hidden="true" />
              ) : (
                <Copy className="w-3.5 h-3.5" aria-hidden="true" />
              )}
              {copied ? "Copied" : "Copy"}
            </button>
            <span role="status" aria-live="polite" className="sr-only">{copied ? "Document copied to clipboard." : ""}</span>
          </div>
          <pre tabIndex={0} aria-label="Generated document content" className="text-sm text-foreground whitespace-pre-wrap font-mono leading-relaxed max-h-[32rem] overflow-y-auto focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
            {result.content}
          </pre>
        </section>
      )}

      {/* Example documents */}
      <section className="esg-examples" aria-labelledby="examples-heading">
        <h2
          id="examples-heading"
          className="text-sm font-semibold text-foreground mb-3"
        >
          Example Documents
        </h2>
        <ExampleDocuments />
      </section>
    </div>
  );
}
